
import React from 'react';

export default function StrategySamplesPage({
  strategyCategories = [],
  selectedStrategyCategory,
  setSelectedStrategyCategory,
  setFormData,
  setActiveTab,
  currentTheme,
  isDarkTheme,
  indicatorSearch,
  setIndicatorSearch,
  indicatorSort,
  setIndicatorSort,
  indicatorCategory
}) {

  // Use a single theme color for all categories
  const mainColor = currentTheme?.primary || '#4285f4';
  const categories = strategyCategories.map(cat => ({
    id: cat.name,
    label: cat.name,
    icon: cat.icon,
    count: cat.strategies.length
  }));

  // Treat both 'all' and 'All' as 'Show All'
  const isShowAll = !selectedStrategyCategory || selectedStrategyCategory.toLowerCase() === 'all';
  const filteredCategories = !isShowAll
    ? strategyCategories.filter(cat => cat.name === selectedStrategyCategory)
    : strategyCategories;

  return (
    <div style={{
      padding: 24,
      backgroundColor: currentTheme?.cardBg,
      color: currentTheme?.textPrimary,
      fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
      border: `1px solid ${currentTheme?.border}`,
      borderRadius: 8,
      boxShadow: isDarkTheme ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.07)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
  <h2 style={{ color: currentTheme?.textPrimary || '#222', fontWeight: 700, fontSize: 22, margin: 0, fontFamily: 'inherit' }}>
          📚 Strategy Samples
        </h2>
        <input
          type="text"
          placeholder="Search strategies..."
          value={indicatorSearch || ''}
          onChange={e => setIndicatorSearch && setIndicatorSearch(e.target.value)}
          style={{
            marginLeft: 16,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: 14,
            minWidth: 180
          }}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {categories.map(cat => {
          const selected = selectedStrategyCategory === cat.label;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedStrategyCategory && setSelectedStrategyCategory(cat.label)}
              style={{
                background: selected ? mainColor : '#f5f5f5',
                color: selected ? '#fff' : '#333',
                border: 'none',
                borderRadius: 6,
                padding: '8px 18px',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                boxShadow: selected ? `0 2px 8px ${mainColor}22` : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                position: 'relative'
              }}
            >
              <span>{cat.icon}</span> {cat.label}
              <span style={{
                background: selected ? '#fff' : mainColor,
                color: selected ? mainColor : '#fff',
                border: `1px solid ${mainColor}`,
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                padding: '2px 8px',
                marginLeft: 8,
                minWidth: 24,
                textAlign: 'center',
                display: 'inline-block',
                transition: 'all 0.2s'
              }}>{cat.count}</span>
            </button>
          );
        })}
        <button
          onClick={() => setSelectedStrategyCategory && setSelectedStrategyCategory('all')}
          style={{
            background: isShowAll ? mainColor : '#f5f5f5',
            color: isShowAll ? '#fff' : '#333',
            border: 'none',
            borderRadius: 6,
            padding: '8px 18px',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
            marginLeft: 8
          }}
        >
          Show All
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
        {filteredCategories.map(cat => (
          <div key={cat.name} style={{
            background: isDarkTheme ? '#23272f' : '#fff',
            border: `2px solid ${mainColor}`,
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 2px 8px #0001',
            minWidth: 320
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>{cat.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 18, color: mainColor }}>{cat.name}</span>
              <span style={{
                background: '#fff',
                color: mainColor,
                border: `1px solid ${mainColor}`,
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                padding: '2px 8px',
                marginLeft: 8,
                minWidth: 24,
                textAlign: 'center',
                display: 'inline-block',
              }}>{cat.strategies.length}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              {cat.strategies
                .filter(s => !indicatorSearch || s.title.toLowerCase().includes(indicatorSearch.toLowerCase()) || s.entry.toLowerCase().includes(indicatorSearch.toLowerCase()) || s.exit.toLowerCase().includes(indicatorSearch.toLowerCase()))
                .map((strategy, idx) => (
                  <div key={strategy.title + idx} style={{
                    background: isDarkTheme ? '#181c22' : '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 4,
                    flex: '1 1 320px',
                    minWidth: 280,
                    maxWidth: 400
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{strategy.title}</div>
                    <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>{strategy.description}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 14, marginBottom: 2 }}>
                      <span style={{ color: '#388e3c', fontWeight: 600 }}>Entry:</span> {strategy.entry}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 14 }}>
                      <span style={{ color: '#b71c1c', fontWeight: 600 }}>Exit:</span> {strategy.exit}
                    </div>
                    <button
                      style={{
                        marginTop: 10,
                        background: mainColor,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 16px',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        float: 'right'
                      }}
                      onClick={() => {
                        setFormData && setFormData(prev => ({
                          ...prev,
                          entryConditions: strategy.entry,
                          exitConditions: strategy.exit
                        }));
                        setActiveTab && setActiveTab('builder');
                      }}
                    >
                      Use in Builder
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
